/* eslint-disable import/no-unused-modules */
/**
 * Centralised image format configuration.
 *
 * This module defines the single source of truth for image upload types,
 * MIME types, and related configuration used across both the new-stack
 * (packages/web) and old-stack (themes/archipro2).
 *
 * Image formats are organised into five tiers:
 * 1. BASE — universally browser-supported formats (jpg, jpeg, png, webp)
 * 2. CONVERTIBLE — formats our compression pipeline can convert to a base
 *    format (e.g. avif → jpg). Always accepted for upload.
 * 3. PROCESSABLE — formats requiring server-side processing infrastructure
 *    (e.g. heic/heif from iPhone cameras). Only accepted when
 *    auto-upscale is enabled.
 * 4. GIF — animated image formats (gif, apng) that bypass compression.
 * 5. DISPLAY_ONLY — other recognised image formats not part of the upload
 *    pipeline (svg, bmp, ico, tif). Used by `isImageFile` for recognition.
 *
 * All format data is defined once in IMAGE_FORMAT_REGISTRY. Every other
 * constant is derived from it, so adding or changing a format only
 * requires editing the registry.
 */

// ---------------------------------------------------------------------------
// Compression format type
// ---------------------------------------------------------------------------

/** Compression output format type used by the image compression library. */
export type CompressionFormat = 'webp' | 'jpeg' | 'png';

/** MIME type corresponding to a compression format (e.g. 'image/jpeg'). */
export type CompressionMimeType = `image/${CompressionFormat}`;

// ---------------------------------------------------------------------------
// ImageTier — classifies each image format
// ---------------------------------------------------------------------------

/** Classification tier for each image format entry. */
type ImageTier = (typeof ImageTier)[keyof typeof ImageTier];
const ImageTier = {
    /** Universally browser-supported formats (jpg, png, webp). */
    Base: 0,
    /** Formats our compression pipeline can convert to a base format (e.g. avif). */
    Convertible: 1,
    /** Formats requiring server-side auto-upscale processing (e.g. heic/heif). */
    Processable: 2,
    /** Animated image formats that bypass compression (gif, apng). */
    Gif: 3,
    /** Other recognised image formats not part of the upload pipeline (svg, bmp, ico, tif). */
    DisplayOnly: 4,
} as const;

// ---------------------------------------------------------------------------
// Format registry — THE single source of truth
// ---------------------------------------------------------------------------

/**
 * Describes a single image format.
 * Every image format we support is declared exactly once here.
 */
interface ImageFormatEntry {
    /** File extension (e.g. 'jpg'). */
    ext: string;
    /** MIME type (e.g. 'image/jpeg'). */
    mime: string;
    /** Classification tier. */
    tier: ImageTier;
    /**
     * Preferred output extension when saving files of this MIME type.
     * Defaults to `ext` when omitted. Use this when multiple extensions
     * share the same MIME (e.g. both 'jpg' and 'jpeg' save as 'jpg').
     */
    preferredExt?: string;
    /** Preferred compression output format (undefined = requires conversion to JPEG). */
    compressionFormat?: CompressionFormat;
    /**
     * Whether this MIME type requires conversion to JPEG before use.
     * Defaults to false.
     */
    requiresConversion?: boolean;
}

// ---------------------------------------------------------------------------
// Size constants
// ---------------------------------------------------------------------------

/** Standard max image upload size in MB. File upload to php can't exceed this size. */
export const IMAGE_UPLOAD_MAX_SIZE_MB = 20;

/**
 * Maximum file size in MB that client-side compression will accept.
 * Files exceeding this limit are rejected outright — they are too large
 * for the browser compression pipeline to handle reliably.
 * Used exclusively by the image compression module.
 */
export const MAX_CLIENT_SIDE_COMPRESSION_SIZE_MB = 256;

/**
 * Minimum file size in MB to trigger compression.
 * Compression will only occur if file size exceeds this value,
 * or if image dimensions exceed maxWidth/maxHeight.
 */
export const IMAGE_NEED_COMPRESSION_SIZE_MB = 10;

/**
 * The default image format entry (JPG) from the registry.
 * Used as the fallback output format for conversions and compression.
 */
export const JPEG_IMAGE_FORMAT: ImageFormatEntry = {
    ext: 'jpg',
    mime: 'image/jpeg',
    tier: ImageTier.Base,
    compressionFormat: 'jpeg',
};
/**
 * Single source of truth for every image format we handle.
 *
 * To add or change a format, edit this array — all exported constants
 * and helpers are derived from it automatically.
 */
const IMAGE_FORMAT_REGISTRY: readonly ImageFormatEntry[] = [
    // --- Base formats (browser-native) ---
    JPEG_IMAGE_FORMAT,
    {
        ext: 'jpeg',
        mime: 'image/jpeg',
        tier: ImageTier.Base,
        preferredExt: 'jpg',
        compressionFormat: 'jpeg',
    },
    {
        ext: 'png',
        mime: 'image/png',
        tier: ImageTier.Base,
        compressionFormat: 'png',
    },
    // --- Convertible formats (compression pipeline converts to base) ---
    {
        ext: 'webp', // webp need to be converted to jpeg for Gigapixel upscale
        mime: 'image/webp',
        tier: ImageTier.Convertible,
        requiresConversion: true,
    },
    {
        ext: 'avif',
        mime: 'image/avif',
        tier: ImageTier.Convertible,
        requiresConversion: true,
    },

    // --- Processable formats (server-side auto-upscale required) ---
    {
        ext: 'heic',
        mime: 'image/heic',
        tier: ImageTier.Processable,
        requiresConversion: true,
    },
    {
        ext: 'heif',
        mime: 'image/heif',
        tier: ImageTier.Processable,
        requiresConversion: true,
    },

    // --- Animated image formats (bypass compression) ---
    { ext: 'gif', mime: 'image/gif', tier: ImageTier.Gif },
    { ext: 'apng', mime: 'image/apng', tier: ImageTier.Gif },

    // --- Display-only formats (recognised but not part of the upload pipeline) ---
    { ext: 'svg', mime: 'image/svg+xml', tier: ImageTier.DisplayOnly },
    { ext: 'bmp', mime: 'image/bmp', tier: ImageTier.DisplayOnly },
    { ext: 'ico', mime: 'image/x-icon', tier: ImageTier.DisplayOnly },
    { ext: 'tif', mime: 'image/tiff', tier: ImageTier.DisplayOnly },
    {
        ext: 'tiff',
        mime: 'image/tiff',
        tier: ImageTier.DisplayOnly,
        preferredExt: 'tif',
    },
] as const;

/**
 * Additional MIME variants that require conversion but don't correspond
 * to a unique file extension (e.g. animated HEIC sequences, JPEG XL).
 * These appear in accept strings and the conversion list only.
 */
const EXTRA_CONVERSION_MIMES = [
    'image/heic-sequence',
    'image/heif-sequence',
    'image/jxl',
] as const;

// ---------------------------------------------------------------------------
// Derived extension arrays
// ---------------------------------------------------------------------------

/** Helper: collect extensions for entries at or below the given tier. */
const extensionsUpTo = (maxTier: ImageTier): readonly string[] =>
    IMAGE_FORMAT_REGISTRY.filter((e) => e.tier <= maxTier).map((e) => e.ext);

/** Universally browser-supported image extensions. */
export const BASE_IMAGE_EXTENSIONS = extensionsUpTo(
    ImageTier.Base,
) as readonly string[];

/**
 * Extensions our compression pipeline can convert to a base format.
 * These are always accepted for upload even if the browser cannot
 * display them natively (e.g. AVIF is converted to JPEG).
 */
export const CONVERTIBLE_IMAGE_EXTENSIONS = extensionsUpTo(
    ImageTier.Convertible,
) as readonly string[];

/**
 * Extensions that require active server-side processing (auto-upscale)
 * to be enabled. These formats are converted to JPEG before use.
 * e.g. HEIC/HEIF from iPhone cameras.
 */
export const PROCESSABLE_IMAGE_EXTENSIONS = extensionsUpTo(
    ImageTier.Processable,
) as readonly string[];

/** Helper: collect extensions for entries matching a specific tier. */
const extensionsForTier = (tier: ImageTier): readonly string[] =>
    IMAGE_FORMAT_REGISTRY.filter((e) => e.tier === tier).map((e) => e.ext);

/**
 * Animated image format extensions (gif, apng).
 * These bypass compression and are passed through as-is.
 */
export const GIF_IMAGE_EXTENSIONS = extensionsForTier(
    ImageTier.Gif,
) as readonly string[];

/**
 * HEIC/HEIF image format extensions (heic, heif).
 * Used to detect iPhone camera images that need conversion.
 */
export const HEIC_EXTENSIONS = extensionsForTier(
    ImageTier.Processable,
) as readonly string[];

/**
 * All HEIC/HEIF related MIME types, including animated sequence variants.
 * Used to detect HEIC files by MIME type when the extension is unreliable.
 */
export const HEIC_MIME_TYPES: readonly string[] = [
    ...IMAGE_FORMAT_REGISTRY.filter(
        (e) => e.tier === ImageTier.Processable,
    ).map((e) => e.mime),
    'image/heic-sequence',
    'image/heif-sequence',
];

/**
 * Every image extension we recognise across all tiers.
 * Used by `isImageFile` to determine whether a filename is an image.
 */
export const ALL_IMAGE_EXTENSIONS = IMAGE_FORMAT_REGISTRY.map(
    (e) => e.ext,
) as readonly string[];

// ---------------------------------------------------------------------------
// Derived MIME type strings (for HTML accept attributes & dropzone configs)
// ---------------------------------------------------------------------------

/** Deduplicated MIME types for entries at or below the given tier. */
const mimesUpTo = (maxTier: ImageTier): string[] => [
    ...new Set(
        IMAGE_FORMAT_REGISTRY.filter((e) => e.tier <= maxTier).map(
            (e) => e.mime,
        ),
    ),
];

/** MIME types for base browser-supported image formats. */
export const BASE_IMAGE_MIME_TYPES = mimesUpTo(ImageTier.Base).join(', ');

/** Build an HTML accept string of MIME types and dot-extensions up to a tier. */
const acceptUpTo = (maxTier: ImageTier): string =>
    [
        ...mimesUpTo(maxTier),
        ...extensionsUpTo(maxTier).map((ext) => `.${ext}`),
    ].join(', ');

/** MIME types accepted when base formats plus file extensions are needed. */
export const BASE_IMAGE_ACCEPT = acceptUpTo(ImageTier.Base);

/**
 * Accept string for base plus convertible formats (WebP, AVIF).
 * Used when auto-upscale is off — matches `getAllowedImageExtensions(false)`.
 */
export const CONVERTIBLE_IMAGE_ACCEPT = acceptUpTo(ImageTier.Convertible);

/**
 * Accept string for convertible formats plus GIF/APNG.
 * Used by variant image and colour swatch file dialogs.
 */
export const CONVERTIBLE_WITH_GIF_IMAGE_ACCEPT = [
    CONVERTIBLE_IMAGE_ACCEPT,
    ...IMAGE_FORMAT_REGISTRY.filter(
        (entry) => entry.tier === ImageTier.Gif,
    ).flatMap((entry) => [entry.mime, `.${entry.ext}`]),
].join(', ');

/**
 * MIME types accepted when auto-upscale processing is enabled.
 * Includes AVIF and HEIC/HEIF variants.
 */
export const PROCESSABLE_IMAGE_ACCEPT = [
    ...mimesUpTo(ImageTier.Base),
    ...extensionsUpTo(ImageTier.Base).map((ext) => `.${ext}`),
    ...mimesUpTo(ImageTier.Processable).filter(
        (m) => !mimesUpTo(ImageTier.Base).includes(m),
    ),
    ...EXTRA_CONVERSION_MIMES,
    ...extensionsUpTo(ImageTier.Processable)
        .filter((ext) => !extensionsUpTo(ImageTier.Base).includes(ext))
        .map((ext) => `.${ext}`),
].join(', ');

/**
 * MIME types as an array, for use with dropzone libraries that expect arrays.
 */
export const BASE_IMAGE_MIME_TYPES_ARRAY = mimesUpTo(
    ImageTier.Base,
) as readonly string[];

/**
 * MIME types up to the Convertible tier (base + webp/avif).
 * Use when uploads accept formats the compression pipeline can convert.
 */
export const CONVERTIBLE_IMAGE_MIME_TYPES_ARRAY = mimesUpTo(
    ImageTier.Convertible,
) as readonly string[];

// ---------------------------------------------------------------------------
// Derived MIME-to-extension & MIME-to-compression-format maps
// ---------------------------------------------------------------------------

/**
 * Maps image MIME types to their preferred file extensions.
 * When a MIME type has multiple extensions (e.g. jpg/jpeg), the
 * `preferredExt` from the registry is used so files always save
 * with a consistent extension. 'image/jpg' is a non-standard alias.
 */
export const MIME_TO_EXTENSION_MAP: Record<string, string> = Object.fromEntries(
    [
        ...IMAGE_FORMAT_REGISTRY.map(
            (e) => [e.mime, e.preferredExt ?? e.ext] as const,
        ),
        ['image/jpg', 'jpg'], // non-standard alias
    ],
);

/**
 * Maps image MIME types to their preferred compression output format.
 * Used by the image compression pipeline to determine which format the
 * library should produce. Types not in this map default to 'jpeg'.
 */
export const MIME_TO_COMPRESSION_FORMAT: Record<string, CompressionFormat> =
    Object.fromEntries(
        IMAGE_FORMAT_REGISTRY.filter(
            (
                e,
            ): e is ImageFormatEntry & {
                compressionFormat: CompressionFormat;
            } => e.compressionFormat !== undefined,
        ).map((e) => [e.mime, e.compressionFormat]),
    );

// ---------------------------------------------------------------------------
// Derived MIME types that require conversion to JPEG
// ---------------------------------------------------------------------------

/**
 * MIME types that must be converted to JPEG before use.
 * Used by the image compression pipeline.
 */
export const MIME_TYPES_REQUIRING_CONVERSION = [
    ...IMAGE_FORMAT_REGISTRY.filter((e) => e.requiresConversion).map(
        (e) => e.mime,
    ),
    ...EXTRA_CONVERSION_MIMES,
] as const;

// ---------------------------------------------------------------------------
// Functions (all at the bottom)
// ---------------------------------------------------------------------------

/**
 * Converts a file size in megabytes to bytes.
 */
export function mbToBytes(mb: number): number {
    return mb * 1024 * 1024;
}

/**
 * Returns the max file selection size in MB depending on whether auto-upscale is enabled.
 * When enabled, users can select larger files which are compressed client-side
 * before being uploaded to the backend (which enforces IMAGE_UPLOAD_MAX_SIZE_MB).
 */
export const getMaxUploadSizeMB = (isAutoUpscaleEnabled: boolean): number =>
    isAutoUpscaleEnabled
        ? MAX_CLIENT_SIDE_COMPRESSION_SIZE_MB
        : IMAGE_UPLOAD_MAX_SIZE_MB;

/**
 * Returns the max file selection size in bytes depending on whether auto-upscale is enabled.
 * When enabled, users can select larger files which are compressed client-side
 * before being uploaded to the backend (which enforces IMAGE_UPLOAD_MAX_SIZE_MB).
 */
export const getMaxUploadSizeBytes = (isAutoUpscaleEnabled: boolean): number =>
    mbToBytes(getMaxUploadSizeMB(isAutoUpscaleEnabled));

/**
 * Returns the allowed image extensions based on whether auto-upscale
 * processing is enabled.
 * @param isAutoUpscaleEnabled Whether the auto-upscale feature is active.
 */
export const getAllowedImageExtensions = (
    isAutoUpscaleEnabled: boolean,
): readonly string[] =>
    isAutoUpscaleEnabled
        ? PROCESSABLE_IMAGE_EXTENSIONS
        : CONVERTIBLE_IMAGE_EXTENSIONS;

/**
 * Returns the appropriate accept string for file inputs and dropzones
 * based on whether auto-upscale is enabled.
 * @param isAutoUpscaleEnabled Whether the auto-upscale feature is active.
 */
export const getAllowedImageAccept = (isAutoUpscaleEnabled: boolean): string =>
    isAutoUpscaleEnabled ? PROCESSABLE_IMAGE_ACCEPT : CONVERTIBLE_IMAGE_ACCEPT;

/**
 * Returns a MIME-to-extension map containing only the formats allowed for
 * upload (same set as getAllowedImageExtensions). Used when validating
 * images by MIME type (e.g. from blob.type) to reject unsupported formats
 * early with a clear "Unsupported image format" message instead of failing
 * later with a generic extension error.
 * @param isAutoUpscaleEnabled Whether the auto-upscale feature is active.
 */
export const getAllowedMimeToExtensionMap = (
    isAutoUpscaleEnabled: boolean,
): Record<string, string> => {
    const allowedExtensions = new Set(
        getAllowedImageExtensions(isAutoUpscaleEnabled),
    );
    const entries = IMAGE_FORMAT_REGISTRY.filter((e) =>
        allowedExtensions.has(e.ext),
    ).map((e) => [e.mime, e.preferredExt ?? e.ext] as const);

    const result: Record<string, string> = Object.fromEntries(entries);

    // Non-standard alias used by some browsers for JPEG
    if (allowedExtensions.has('jpg')) {
        result['image/jpg'] = 'jpg';
    }

    // When upscale enabled, add HEIC/HEIF sequence MIME variants
    if (isAutoUpscaleEnabled) {
        result['image/heic-sequence'] = 'heic';
        result['image/heif-sequence'] = 'heif';
    }

    return result;
};

/**
 * Returns a human-readable label listing the allowed extensions,
 * useful for error messages.
 * @param isAutoUpscaleEnabled Whether the auto-upscale feature is active.
 * @example "JPG, JPEG, PNG, WEBP, AVIF, HEIC, or HEIF"
 */
export const getAllowedExtensionsLabel = (
    isAutoUpscaleEnabled: boolean,
): string => {
    const extensions = getAllowedImageExtensions(isAutoUpscaleEnabled);
    const upper = extensions.map((ext) => ext.toUpperCase());
    if (upper.length <= 1) return upper.join('');
    return `${upper.slice(0, -1).join(', ')}, or ${upper[upper.length - 1]}`;
};

/** Dot-prefixed extensions for fast `isImageFile` lookups. */
const IMAGE_EXT_SET = new Set(ALL_IMAGE_EXTENSIONS.map((ext) => `.${ext}`));

/**
 * Check if a file is an image by its extension.
 * Matches against every format in the IMAGE_FORMAT_REGISTRY
 * (base, convertible, processable, gif, and display-only).
 * @param filename The name (or path) of the file to check.
 * @returns Whether the file has a recognised image extension.
 */
export function isImageFile(
    filename: string | null | undefined,
    excludeExtensions: string[] = ['bmp', 'tif', 'tiff'],
): boolean {
    if (!filename) {
        return false;
    }
    const dotIdx = filename.lastIndexOf('.');
    if (dotIdx === -1) {
        return false;
    }
    const ext = filename.slice(dotIdx + 1).toLowerCase();
    if (excludeExtensions.some((e) => e.toLowerCase() === ext)) {
        return false;
    }
    return IMAGE_EXT_SET.has(`.${ext}`);
}
